import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template141: Tiendas originales en Seúl
// Contenido desde page (texts, images, canvasImages, data.shops). Sin fallbacks.
const Template141 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const daisoSticker = getCanvasImageUrl("daiso-sticker");
	const logoImage = getCanvasImageUrl("logo");
	const shops = Array.isArray(page?.shops)
		? page.shops
		: Array.isArray(page?.data?.shops)
			? page.data.shops
			: Array.isArray(page?.data?.data?.shops)
				? page.data.data.shops
				: [];
	const intro = page?.texts?.[1]?.content;
	const tipDaiso = page?.texts?.[2]?.content;
	const textFinal = page?.texts?.[3]?.content;

	let actualHeaderColor = headerBackgroundColor || "#2E8B80";
	const contentColor = textColor || "#444";

	const h4Style = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 12,
		fontWeight: 600,
		margin: "6px 0 4px",
		textAlign: "left",
	};

	const pStyle = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{intro && (
				<Box data-block-id="text-1" sx={{ padding: "16px 16px", fontSize: 13, color: contentColor }}>
					<Box component="p" sx={pStyle}>
						{intro}
					</Box>
				</Box>
			)}

			<Box
				data-block-id="text-2"
				sx={{
					padding: "0 12px 12px",
					display: "flex",
					flexWrap: "wrap",
					gap: "12px",
					position: "relative",
					justifyContent: "center",
				}}
			>
				{shops.map((shop) => {
					const src = getImageSrc(shop.imageId);
					return (
						<Box
							key={shop.name}
							sx={{
								backgroundColor: "#fff",
								borderRadius: "12px",
								width: isSmallScreen ? "calc(50% - 6px)" : "calc(33.33% - 8px)",
								minWidth: 120,
								padding: "8px",
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
								border: "1px solid #eee",
							}}
						>
							{src && (
								<img
									src={src}
									alt={shop.name}
									style={{
										width: "100%",
										height: 70,
										objectFit: "cover",
										borderRadius: 8,
									}}
								/>
							)}
							<Box component="h4" sx={h4Style}>
								{shop.name}
							</Box>
							{shop.address && (
								<Box
									component="p"
									sx={{ ...pStyle, textAlign: "left", fontSize: 11 }}
								>
									{shop.address}
								</Box>
							)}
						</Box>
					);
				})}

				{tipDaiso && (
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? -120 : 0,
							left: 0,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 100,
							zIndex: 999,
						}}
					>
						{tipDaiso}
					</Box>
				)}
				{daisoSticker && (
					<img
						src={daisoSticker}
						alt="sticker"
						style={{
							width: isSmallScreen ? 90 : 90,
							position: "absolute",
							bottom: isSmallScreen ? -190 : -60,
							left: -10,
						}}
					/>
				)}
			</Box>

			{textFinal && (
				<Box
					data-block-id="text-3"
					sx={{
						padding: "0 16px 20px",
						textAlign: "center",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: contentColor,
							margin: 0,
							maxWidth: isSmallScreen ? 200 : 380,
							marginLeft: isSmallScreen ? 10 : 0,
						}}
						dangerouslySetInnerHTML={{ __html: textFinal }}
					/>
				</Box>
			)}
		</Box>
	);
};

Template141.thumbnail = "/images/template-1.png";

export default Template141;
